import React, { PropsWithoutRef, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { SIDEBAR_WIDTH } from "../constants";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MenuIcon from "@mui/icons-material/Menu";
import { useLogout, useGetProfile } from "../api/hooks/useAuth";
import { useAuthActions, useAuth, useToken } from "../state/hooks";
import { showSuccess, showError } from "../helpers/toast";
import { remove } from "../helpers/storage";
import { STORAGE_KEYS } from "../constants/storage";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { getQueryClient } from "../api/queryClient";
import { queryTags } from "../api/queryTags";
import { useGetAdminNotifications } from "../api/hooks/useNotification";

interface Props {
  handleSidebarToggle: () => void;
}

function Topbar({ handleSidebarToggle }: PropsWithoutRef<Props>) {
  const navigate = useNavigate();
  const { reset, setUser } = useAuthActions();
  const user = useAuth();
  const [isArabic, setIsArabic] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Fetch profile data - always refetch to ensure fresh data
  const { data: profileData, refetch: refetchProfile } = useGetProfile();

  // Update user state when profile data is loaded
  // Always update with full profile data to ensure role and permissions are set
  useEffect(() => {
    if (profileData?.data) {
      // Always update with full profile data (includes role, permissionId, etc.)
      // This ensures permissions are always up-to-date
      setUser(profileData.data);
    }
  }, [profileData, setUser]);

  // Refetch profile when token changes (after login)
  const token = useToken();
  useEffect(() => {
    if (token) {
      // Refetch profile after login to get fresh data with role/permissions
      refetchProfile();
    }
  }, [token, refetchProfile]);

  // Use profile data - prioritize user state (updated immediately) over query data
  // This ensures Topbar shows updated image immediately after profile edit
  const profile = user || profileData?.data;

  useEffect(() => {
    if (window) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const [scroll, setScroll] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  const handleClick1 = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
  };

  const { data: adminNotificationsData, isLoading: isNotificationsLoading } = useGetAdminNotifications(
    {
      page: 1,
      limit: 5,
    },
    {
      refetchInterval: 2 * 60 * 1000,
      refetchIntervalInBackground: true,
    }
  );
  const adminNotifications = adminNotificationsData?.data?.list || [];

  const formatNotificationTime = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    const diffMs = Date.now() - date.getTime();
    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;

    if (diffMs < minuteMs) return "Just now";
    if (diffMs < hourMs) {
      const minutes = Math.floor(diffMs / minuteMs);
      return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
    }
    if (diffMs < dayMs) {
      const hours = Math.floor(diffMs / hourMs);
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    }
    const days = Math.floor(diffMs / dayMs);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScroll(window.scrollY > 10);
    });
  }, []);

  const { mutate: logout, isPending: isLoggingOut } = useLogout({
    onSuccess: async () => {
      showSuccess("Logged out successfully");
      // Clear auth state first
      reset();
      // Clear storage
      await remove(STORAGE_KEYS.token);
      await remove(STORAGE_KEYS.user);
      // Clear profile query cache to ensure fresh data on next login
      getQueryClient().removeQueries({ queryKey: [queryTags.profile] });
      // Close dialog
      setLogoutDialogOpen(false);
      // Navigate to login
      navigate("/");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to logout. Please try again.";
      showError(errorMessage);
    },
  });

  const handleLogoutClick = () => {
    handleClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    logout();
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <Box className="site_header">
      <AppBar
        position="fixed"
        className="topbar"
        sx={{
          width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          ml: { lg: `${SIDEBAR_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <div className="topbar_left">
            <IconButton onClick={handleSidebarToggle}>
              <MenuIcon />
            </IconButton>
          </div>
          <div className="topbar_right">
            {/* <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              className="lang_icon"
            >
              <Typography variant="body2" className={!isArabic ? "active" : ""}>
                EN
              </Typography>
              <IOSSwitch
                defaultChecked
                onChange={() => setIsArabic((prev) => !prev)}
                checked={isArabic}
              />
              <Typography variant="body2" className={isArabic ? "active" : ""}>
                AR
              </Typography>
            </Box> */}
            <Box>
              <IconButton
                className="icon_btn"
                aria-controls={open1 ? "basic-menu1" : undefined}
                aria-haspopup="true"
                aria-expanded={open1 ? "true" : undefined}
                onClick={handleClick1}
              >
                <NotificationsActiveIcon />
              </IconButton>
              <Menu
                className="notiDropdown_menu"
                id="basic-menu1"
                anchorEl={anchorEl1}
                open={open1}
                onClose={handleClose1}
                MenuListProps={{
                  "aria-labelledby": "basic-button1",
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    width: 380,
                    maxWidth: "92vw",
                    borderRadius: 2,
                    overflow: "hidden",
                    mt: 1,
                    boxShadow: "0px 10px 30px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Box
                  className="noti_head"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #eef0f4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography component="h2">Notifications</Typography>
                  <Button
                    className="btn btn_primary sm"
                    onClick={() => {
                      navigate("/recieved-notifications");
                      handleClose1();
                    }}
                  >
                    See All
                  </Button>
                </Box>
                <ul
                  className="noti_list"
                  style={{
                    margin: 0,
                    padding: "8px",
                    maxHeight: "420px",
                    overflowY: "auto",
                    listStyle: "none",
                  }}
                >
                  {isNotificationsLoading ? (
                    <li>
                      <div
                        className="noti_info"
                        style={{
                          padding: "18px 12px",
                          borderRadius: 10,
                          background: "#f8fafc",
                          textAlign: "center",
                        }}
                      >
                        <Typography component="p">Loading notifications...</Typography>
                      </div>
                    </li>
                  ) : adminNotifications.length > 0 ? (
                    adminNotifications.map((notification: any) => (
                      <li
                        key={notification._id}
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "flex-start",
                          padding: "12px",
                          marginBottom: "8px",
                          border: "1px solid #eef0f4",
                          borderRadius: "10px",
                          background: "#fff",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate("/recieved-notifications");
                          handleClose1();
                        }}
                      >
                        <figure style={{ margin: 0, flexShrink: 0 }}>
                          <img src="/static/images/user_placeholder.png" alt="" />
                        </figure>
                        <div className="noti_info" style={{ minWidth: 0, width: "100%" }}>
                          <Typography
                            component="h3"
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            {notification.title || "Notification"}
                            <Typography component="span" sx={{ color: "#7a7a7a", fontSize: 12, flexShrink: 0 }}>
                              {formatNotificationTime(notification.createdAt)}
                            </Typography>
                          </Typography>
                          <Typography
                            component="p"
                            sx={{
                              color: "#5f6370",
                              fontSize: 13,
                              lineHeight: 1.45,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {notification.message || "No message available"}
                          </Typography>
                        </div>
                      </li>
                    ))
                  ) : (
                    <li>
                      <div
                        className="noti_info"
                        style={{
                          padding: "18px 12px",
                          borderRadius: 10,
                          background: "#f8fafc",
                          textAlign: "center",
                        }}
                      >
                        <Typography component="p">No notifications found.</Typography>
                      </div>
                    </li>
                  )}
                </ul>
              </Menu>
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                  {profile?.fullName && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "inherit",
                        lineHeight: 1.2,
                      }}
                    >
                      {profile.fullName}
                    </Typography>
                  )}
                  {profile?.email && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "12px",
                        color: "inherit",
                        opacity: 0.7,
                        lineHeight: 1.2,
                      }}
                    >
                      {profile.email}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  className="topbar_user_btn"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleClick}
                >
                  <img
                    src={
                      profile?.image ||
                      "/static/images/user_placeholder.png"
                    }
                    alt="Profile"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                </IconButton>
              </Box>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem
                  onClick={() => {
                    navigate("/manage-profile");
                    handleClose();
                  }}
                >
                  <PersonIcon /> My Profile
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate("/manage-profile/change-password");
                    handleClose();
                  }}
                >
                  <LockIcon /> Change Password
                </MenuItem>
                <MenuItem onClick={handleLogoutClick}>
                  <LogoutIcon /> Logout
                </MenuItem>
              </Menu>
            </Box>
          </div>
        </Toolbar>
      </AppBar>

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        isLoading={isLoggingOut}
        loadingText="Logging out..."
      />
    </Box>
  );
}

export default Topbar;
